<template>
    <div
        :id="item.id"
        :aria-labelledby="`${item.id}-tab`"
        role="tabpanel"
        class="tab-pane fade"
    >
        <section class="fparam-panel">
            <FormTabHeader
                icon="fal fa-cogs"
                :title="item.name"
                :subtitle="parametrageSubtitle"
            />
            <div v-if="form && form.statuses && form.statuses.length && allActionTypes.length" class="fparam-scroll">
            <table class="matrix-table">
                <thead>
                    <tr>
                        <th class="matrix-th-label">
                            {{ $t('forms.status.name') }}
                        </th>
                        <th
                            v-for="actionType in allActionTypes"
                            :key="'th-' + actionType"
                            class="matrix-th-action"
                        >
                            <div class="action-badge">
                                <i :class="getActionIcon(actionType)" class="action-badge-icon"></i>
                                {{ actionType }}
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="status in form.statuses"
                        :key="status.id"
                        class="matrix-row"
                    >
                        <td class="matrix-cell-status">
                            <div class="status-info">
                                <div class="status-icon-box">
                                    <i :class="status.icon"></i>
                                </div>
                                <span class="status-name">{{ status.name }}</span>
                                <span
                                    class="status-badge"
                                    :class="status.active ? 'status-badge--active' : 'status-badge--inactive'"
                                >
                                    {{ status.active ? $t('forms.status.active') : $t('forms.parametrage.inactive') }}
                                </span>
                            </div>
                        </td>
                        <td
                            v-for="actionType in allActionTypes"
                            :key="status.id + '-' + actionType"
                            class="matrix-cell-toggle"
                        >
                            <template v-if="getAction(status, actionType)">
                                <label class="toggle">
                                    <input
                                        type="checkbox"
                                        :checked="getAction(status, actionType).active"
                                        @change="toggleAction(status, getAction(status, actionType))"
                                    />
                                    <span class="toggle-slider"></span>
                                </label>
                            </template>
                            <span v-else class="no-action">—</span>
                        </td>
                    </tr>
                </tbody>
            </table>
            </div>

            <div v-else class="parametrage-empty">
                <i class="fal fa-cogs parametrage-empty-icon"></i>
                <p>{{ $t('forms.parametrage.no_statuses') }}</p>
            </div>
        </section>
    </div>
</template>

<script>
import FormMixin from './FormMixin'
import FormTabHeader from './FormTabHeader'
import { mapActions } from 'vuex'

export default {
    name: 'FormParametrage',
    mixins: [FormMixin],
    components: { FormTabHeader },
    props: ['item'],
    computed: {
        allActionTypes() {
            if (!this.form || !this.form.statuses) return []
            const set = new Set()
            this.form.statuses.forEach((status) => {
                (status.actions || []).forEach((action) => set.add(action.type))
            })
            return Array.from(set)
        },
        parametrageSubtitle() {
            if (!this.form || !this.form.statuses) return ''
            const statusCount = this.form.statuses.length
            const actionLabel = this.$t('forms.parametrage.status_count') || 'statuts'
            return `${statusCount} ${actionLabel} · ${this.allActionTypes.length} action(s)`
        }
    },
    methods: {
        ...mapActions('form', ['updateStatusAction', 'getForm']),
        getActionIcon(type) {
            const t = (type || '').toLowerCase()
            if (t.includes('email') || t.includes('mail')) return 'fal fa-envelope'
            if (t.includes('notification') || t.includes('notify') || t.includes('notif')) return 'fal fa-bell'
            if (t.includes('sms')) return 'fal fa-mobile'
            if (t.includes('webhook') || t.includes('hook')) return 'fal fa-link'
            if (t.includes('push')) return 'fal fa-paper-plane'
            if (t.includes('assign') || t.includes('attribu')) return 'fal fa-user-plus'
            if (t.includes('close') || t.includes('fermer')) return 'fal fa-times-circle'
            if (t.includes('reopen') || t.includes('rouvrir')) return 'fal fa-redo'
            if (t.includes('escalat')) return 'fal fa-arrow-up'
            if (t.includes('comment')) return 'fal fa-comment'
            if (t.includes('archiv')) return 'fal fa-archive'
            if (t.includes('export')) return 'fal fa-download'
            return 'fal fa-bolt'
        },
        getAction(status, actionType) {
            if (!status.actions) return null
            return status.actions.find((a) => a.type === actionType) || null
        },
        toggleAction(status, action) {
            this.updateStatusAction({
                formId: this.form.id,
                statusId: status.id,
                action: { ...action, active: !action.active }
            })
                .then(() => {
                    this.getForm(this.form.id)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title')
                    })
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
        }
    }
}
</script>

<style scoped>
/* ── Header ── */
/* ── Matrix ── */
.fparam-panel {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    margin-top: var(--v2-space-5);
}

.fparam-scroll {
    overflow-x: auto;
}

.matrix-table {
    width: 100%;
    border-collapse: collapse;
}

.matrix-th-label {
    text-align: left;
    padding: 14px 20px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    min-width: 240px;
}

.matrix-th-action {
    text-align: center;
    padding: 14px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    min-width: 120px;
}

.action-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    background: #0891b2;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 500;
}

.action-badge-icon {
    font-size: 0.75rem;
    color: #fff;
}

/* ── Rows ── */
.matrix-row {
    transition: background 0.1s ease;
}

.matrix-row:hover {
    background: #f8fafc;
}

.matrix-cell-status {
    padding: 12px 20px;
    border-bottom: 1px solid #f1f5f9;
}

.status-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.status-icon-box {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: #ecfeff;
    color: #0891b2;
    font-size: 0.8rem;
    flex-shrink: 0;
}

.status-name {
    font-weight: 600;
    font-size: 0.875rem;
    color: #0f172a;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 0.65rem;
    font-weight: 600;
}

.status-badge--active {
    background: #dcfce7;
    color: #166534;
}

.status-badge--inactive {
    background: #fee2e2;
    color: #991b1b;
}

.matrix-cell-toggle {
    text-align: center;
    padding: 12px 16px;
    border-bottom: 1px solid #f1f5f9;
}

.no-action {
    color: #e2e8f0;
    font-size: 0.85rem;
}

/* ── Toggle switch ── */
.toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
}

.toggle input {
    display: none;
}

.toggle-slider {
    position: relative;
    width: 36px;
    height: 20px;
    background: #cbd5e1;
    border-radius: 20px;
    transition: all 0.2s ease;
}

.toggle-slider::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle input:checked + .toggle-slider {
    background: #0891b2;
}

.toggle input:checked + .toggle-slider::after {
    transform: translateX(16px);
}

.toggle:hover .toggle-slider {
    background: #94a3b8;
}

.toggle:hover input:checked + .toggle-slider {
    background: #0e7490;
}

/* ── Empty state ── */
.parametrage-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 20px;
    color: #94a3b8;
    gap: 12px;
}

.parametrage-empty-icon {
    font-size: 2.5rem;
}

/* ── Responsive ── */
@media (max-width: 768px) {
    .matrix-th-label {
        padding: 12px 16px;
        min-width: 180px;
    }

    .matrix-th-action {
        padding: 12px 10px;
        min-width: 100px;
    }

    .matrix-cell-status {
        padding: 10px 16px;
    }

    .matrix-cell-toggle {
        padding: 10px 12px;
    }
}
</style>
